import { type JSX } from 'react'
import { twJoin } from 'tailwind-merge'

import ProductImage from '@components/ProductImage'
import { useIsWideLayout } from '@hooks/useMediaQuery.ts'
import useTypedTranslation from '@hooks/useTypedTranslation.ts'
import { useProductSearchByGTIN } from '@state/product.ts'
import { cx } from '@utils/cx.ts'

interface ProductDetailsCardProps {
  selectedGtin?: string
  additionalBlocks?: JSX.Element[]
  utilities?: JSX.Element[]
}

function ProductDetailsCard({ selectedGtin, additionalBlocks, utilities }: ProductDetailsCardProps) {
  const { t } = useTypedTranslation()
  const isWideLayout = useIsWideLayout()

  const { data: productsByGtin } = useProductSearchByGTIN(!isWideLayout ? selectedGtin : undefined)

  if (isWideLayout || !productsByGtin) return null

  const items = productsByGtin.items
  const product = items.length ? items[0] : undefined

  if (!product) {
    return <Skeleton />
  }

  const aggregatedStockLevel = product.aggregateStockLevel('optimistic')

  const stockIndicator = cx(
    'absolute top-4 left-0 h-12 w-1 rounded-r-full',
    aggregatedStockLevel === 'HAS_STOCK' && 'bg-has-stock',
    aggregatedStockLevel === 'NO_STOCK' && 'bg-no-stock',
    aggregatedStockLevel === 'UNKNOWN' && 'bg-unknown-stock'
  )

  return (
    <div className="absolute bottom-0 border border-t-2 border-border-secondary bg-bg-primary" data-testid='product-details-card'>
      <div className={stockIndicator} />
      <div className='flex h-full flex-col gap-3 pt-5 pb-6'>
        <div className='flex gap-3 px-4'>
          <div className='w-14'>
            <ProductImage src={product.imageUrl} />
          </div>

          <div>
            <div className='line-clamp-2 text-md font-medium wrap-break-word'>{product.name}</div>
            <div className='text-md text-text-secondary'>{product.brandName}</div>
          </div>
        </div>

        <div className='flex h-16 w-screen gap-3 overflow-x-auto px-4'>
          {additionalBlocks ?? null}

          {product.inventory && (
            <Block label={t('common.stock')} value={String(product.inventory.quantity)} />
          )}
          <Block label="Kategorie" value={product.baseCategory} />
          <Block label="Preis" value={product.getPriceTag('reported')} />
          <Block label="GTIN" value={product.gtin} />
        </div>

        {utilities ?? null}
      </div>
    </div>
  )
}

export function Block({ label, value }: {label: string; value?: string}) {
  return (
    <div className='h-full max-w-40 rounded-md bg-bg-tertiary-subtle px-5 py-3'>
      <p className='text-sm text-text-secondary'>{label}</p>
      {value ?
        <p className='overflow-hidden text-md text-nowrap text-ellipsis' title={value}>{value}</p> :
        <p className='text-center'>-</p>
      }
    </div>
  )
}

function Skeleton() {
  const blockClasses = twJoin(`
    flex h-full animate-pulse flex-col items-center justify-center rounded-md bg-bg-tertiary-subtle px-5 py-3
  `)

  return (
    <div className={twJoin('relative flex h-44 flex-col border-t-2 border-border-secondary bg-bg-primary')}>
      <div className='flex h-full flex-col gap-3 px-4 pt-5 pb-6'>
        <div className='flex gap-3'>
          <div className={twJoin('size-14 animate-pulse rounded-sm bg-bg-tertiary-subtle')} />
          <div className='flex flex-1 flex-col gap-2'>
            <div className={twJoin('h-5 w-3/4 animate-pulse rounded-sm bg-bg-tertiary-subtle')} />
            <div className={twJoin('h-4 w-1/2 animate-pulse rounded-sm bg-bg-tertiary-subtle')} />
          </div>
        </div>
        <div className='flex h-full gap-3'>
          <div className={twJoin(blockClasses, 'w-32')} />
          <div className={twJoin(blockClasses, 'w-24')} />
          <div className={twJoin(blockClasses, 'w-30')} />
        </div>
      </div>
    </div>
  )
}

export default ProductDetailsCard